import type {
  ChatBotSettingsFormValues,
  ChatBotUIConversationMessage,
  ChatBotUIMessage,
  ChatBotUIRef,
  ChatBotUISendMessagePayload,
} from "@jugl-web/domain-resources/chat-bot";
import { ChatBotUI } from "@jugl-web/domain-resources/chat-bot";
import {
  useAgentImageSource,
  useChatImageSource,
} from "@jugl-web/domain-resources/chat-bot/hooks/chatBotMediaSourceResolvers";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { SupportedChatMessagePayloadActions } from "@jugl-web/rest-api/integrations/models/ChatBotMessage";
import { DataLoadingWrapper } from "@jugl-web/ui-components";
import { cx, getUniqueId, useToast, useTranslations } from "@jugl-web/utils";
import { createPlaygroundMessagesStoreHook } from "@web-src/modules/cx/utils/createPlaygroundMesagesStoreHook";
import {
  FC,
  useCallback,
  useDeferredValue,
  useMemo,
  useRef,
  useState,
} from "react";
import { BackdropContainer } from "./components/BackdropContainer";
import { ButtonPositionIndicators } from "./components/ButtonPositionIndicators";
import { CTAPreview } from "./components/CTAPreview";
import { ChatBotButton } from "./components/ChatBotButton";
import {
  BUTTON_SIZE,
  INITIAL_INCOMING_MESSAGE,
  MOCKED_OUTGOING_MESSAGE,
  PREVIEW_USER_NAME,
  WELCOME_MESSAGE_ID,
} from "./consts";

export interface ChatbotChannelPreviewProps
  extends Omit<ChatBotSettingsFormValues, "ctaVisualEnabled"> {
  entityId: string;
  chatMode?: "active" | "mocked";
  previewMode?: "default" | "launcher" | "ctas" | "suggestions" | "chat-only";
  onRevise?: (params: { question: string; agentAnswer: string }) => void;
}

const useChatBotChannelPreviewMessagesStore =
  createPlaygroundMessagesStoreHook<ChatBotUIConversationMessage>([
    INITIAL_INCOMING_MESSAGE,
  ]);

export const ChatBotChannelPreview: FC<ChatbotChannelPreviewProps> = ({
  entityId,
  chatName,
  agentName,
  welcomeMessage,
  senderMessageBackgroundColor,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  theme,
  buttonColor,
  alignment,
  bottomSpacing,
  sideSpacing,
  chatImage,
  agentImage,
  buttonIcon,
  ctas,
  ctaVisual,
  suggestions,
  chatMode = "active",
  previewMode = "default",
  onRevise,
}) => {
  const { t } = useTranslations();
  const { toast } = useToast();

  const { integrationsApi, aiWorkflowsApi } = useRestApiProvider();

  const [sendAgentChatMessage, { isLoading: isSendAgentChatMessageLoading }] =
    aiWorkflowsApi.useSendAgentChatMessageMutation();

  const { data: agentConfig, isLoading: isAgentConfigLoading } =
    integrationsApi.useGetAiAgentConfigQuery({
      entityId,
    });

  /**
   * Defer the form-driven props so that typing in any input field does
   * not force an immediate re-render of the potentially-large message list.
   */
  const deferredAgentName = useDeferredValue(agentName);
  const deferredAgentImage = useDeferredValue(agentImage);
  const deferredWelcomeMessage = useDeferredValue(welcomeMessage);

  const chatImageSrc = useChatImageSource(chatImage);
  const agentImageSrc = useAgentImageSource(deferredAgentImage);

  const activeChatMessagesSource =
    useChatBotChannelPreviewMessagesStore(entityId);

  const mockedChatMessagesSource = useMemo(() => {
    return {
      messages: [INITIAL_INCOMING_MESSAGE, MOCKED_OUTGOING_MESSAGE],
      setMessages: () => {},
    };
  }, []);

  const { messages, setMessages } =
    chatMode === "active" ? activeChatMessagesSource : mockedChatMessagesSource;

  const chatBotUIRef = useRef<ChatBotUIRef>(null);

  const [isResetting, setIsResetting] = useState(false);

  /** Reset to a fresh welcome message; display values are re-applied by displayMessages. */
  const resetMessages = useCallback(async () => {
    if (chatMode === "mocked") {
      return;
    }

    try {
      setIsResetting(true);

      await sendAgentChatMessage({
        entityId,
        // Agent will reset the context once we type "reset" in the chat
        input: "reset",
        medium: "chatbot",
        business_name: agentName,
        knowledge_id: entityId,
        agent_id: agentConfig?.agent_id || entityId,
        instructions: agentConfig?.config.instructions[0]?.value || "",
      }).unwrap();

      setMessages([INITIAL_INCOMING_MESSAGE]);
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    } finally {
      setIsResetting(false);
    }
  }, [
    agentConfig?.agent_id,
    agentConfig?.config.instructions,
    agentName,
    chatMode,
    entityId,
    sendAgentChatMessage,
    setMessages,
    t,
    toast,
  ]);

  const filteredSuggestions = useMemo(
    () => suggestions.map((s) => s.value.trim()).filter((v) => v.length > 0),
    [suggestions]
  );

  const hasConversationJustStarted = useMemo(
    () =>
      messages.length === 0 ||
      (messages.length === 1 && messages[0].id === WELCOME_MESSAGE_ID),
    [messages]
  );

  const handleSendMessage = useCallback(
    async (payload: ChatBotUISendMessagePayload) => {
      if (chatMode === "mocked") {
        return;
      }

      let plainText = "";
      let agentInput = "";

      if (payload.type === "text") {
        plainText = payload.text;
        agentInput = payload.text;
      } else if (payload.type === "submit-form") {
        const json = {
          action: "submit_form",
          form_data: payload.data.reduce<Record<string, string>>(
            (acc, field) => {
              acc[field.name] = field.value;
              return acc;
            },
            {}
          ),
        } satisfies SupportedChatMessagePayloadActions["SubmitFormAction"];

        plainText =
          Object.values(json.form_data)
            .filter((value) => (value as string).trim().length > 0)
            .join(", ") || "(no data)";
        agentInput = JSON.stringify(json);
      } else if (payload.type === "add-to-cart") {
        const json = {
          action: "add_to_cart",
          product: {
            id: payload.data.product.id,
            name: payload.data.product.name,
            quantity: payload.data.quantity,
          },
        } satisfies SupportedChatMessagePayloadActions["AddToCartAction"];

        plainText = `Add ${payload.data.quantity} x ${payload.data.product.name} to cart`;
        agentInput = JSON.stringify(json);
      } else {
        return;
      }

      const optimisticId = getUniqueId();

      const optimisticMessage: ChatBotUIMessage = {
        id: optimisticId,
        type: "outgoing",
        timestamp: new Date(),
        payload: {
          type: "text",
          text: plainText,
          quickReplies: [],
        },
        senderName: PREVIEW_USER_NAME,
        senderImageUrl: "",
      };

      setMessages((prev) => [...prev, optimisticMessage]);

      window.setTimeout(() => {
        chatBotUIRef.current?.scrollToLastMessage();
      }, 100);
      try {
        const resp = await sendAgentChatMessage({
          entityId,
          input: agentInput,
          medium: "chatbot",
          business_name: agentConfig?.config.business_name || "",
          knowledge_id: entityId,
          agent_id: agentConfig?.agent_id || entityId,
          instructions: agentConfig?.config.instructions[0]?.value || "",
        }).unwrap();

        const agentMessage: ChatBotUIMessage = {
          id: getUniqueId(),
          type: "incoming",
          timestamp: new Date(),
          payload:
            resp.cta_tool_result?.type === "inputs"
              ? {
                  type: "inputs",
                  text: resp.agent_response,
                  inputs: resp.cta_tool_result.content.map((input) => ({
                    name: input.title,
                    type: input.type === "phone" ? "tel" : input.type,
                    isRequired: input.title === "Name",
                  })),
                }
              : {
                  type: "text",
                  text: resp.agent_response,
                  quickReplies: resp.quick_replies ?? [],
                },
          senderName: agentName || "Support",
          senderImageUrl: agentImageSrc,
        };

        setMessages((prev) => [...prev, agentMessage]);

        window.setTimeout(() => {
          chatBotUIRef.current?.scrollToLastMessage();
        }, 100);
      } catch {
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId));

        toast(
          t({
            id: "feedback.something-went-wrong",
            defaultMessage: "Something went wrong",
          }),
          { variant: "error" }
        );
      }
    },
    [
      chatMode,
      setMessages,
      sendAgentChatMessage,
      entityId,
      agentConfig?.config.business_name,
      agentConfig?.config.instructions,
      agentConfig?.agent_id,
      agentName,
      agentImageSrc,
      toast,
      t,
    ]
  );

  const displayMessages = useMemo(
    () =>
      messages.map<ChatBotUIConversationMessage>((message) => {
        if (message.type === "outgoing") {
          return message;
        }

        return {
          ...message,
          senderName: deferredAgentName,
          senderImageUrl: agentImageSrc,
          ...(message.id === "welcome"
            ? {
                payload: {
                  ...message.payload,
                  text: deferredWelcomeMessage,
                },
              }
            : {}),
        };
      }),
    [messages, deferredAgentName, agentImageSrc, deferredWelcomeMessage]
  );

  return (
    <DataLoadingWrapper isLoading={isAgentConfigLoading}>
      {/* Main container */}
      <div className="flex h-full w-full justify-center py-6">
        {/* Inner container */}
        <BackdropContainer
          alignment={alignment}
          isBackdropVisible={
            previewMode === "launcher" || previewMode === "ctas"
          }
          className={cx(
            "my-auto",
            previewMode === "ctas" && "h-auto min-h-[300px] justify-end"
          )}
        >
          {/* Chatbot Frame */}
          {previewMode !== "ctas" && (
            <div className="h-full w-full max-w-[410px] overflow-hidden rounded-3xl shadow-[0_8px_24px_rgba(0,0,0,0.15)]">
              <ChatBotUI
                ref={chatBotUIRef}
                messages={displayMessages}
                suggestions={
                  hasConversationJustStarted || previewMode === "suggestions"
                    ? filteredSuggestions
                    : undefined
                }
                config={{
                  headerTitle: chatName,
                  userBubbleColor: senderMessageBackgroundColor,
                  agentName: deferredAgentName,
                  agentImageUrl: agentImageSrc,
                  chatImageUrl: chatImageSrc,
                }}
                user={{ name: PREVIEW_USER_NAME }}
                isInitialLoading={isResetting}
                isTyping={isSendAgentChatMessageLoading}
                isInputDisabled={chatMode === "mocked"}
                isSending={isSendAgentChatMessageLoading}
                onSendMessage={handleSendMessage}
                onRestart={chatMode === "active" ? resetMessages : undefined}
                onRevise={onRevise}
              />
            </div>
          )}
          {previewMode === "ctas" && (
            <CTAPreview
              ctas={ctas}
              ctaVisual={ctaVisual}
              alignment={alignment}
              buttonSize={BUTTON_SIZE}
            />
          )}
          {/* Launcher button */}
          {previewMode !== "chat-only" && (
            <ChatBotButton
              isOpen={false}
              icon={buttonIcon}
              buttonColor={buttonColor}
              className={cx(
                "z-10",
                alignment === "right" ? "self-end" : "self-start"
              )}
            />
          )}
          {/* Vectors */}
          {previewMode === "launcher" && (
            <ButtonPositionIndicators
              alignment={alignment}
              bottomSpacing={bottomSpacing}
              sideSpacing={sideSpacing}
            />
          )}
        </BackdropContainer>
      </div>
    </DataLoadingWrapper>
  );
};
